/**
 * LoginPage — username + password sign-in.
 *
 * Form is built with react-hook-form + Zod for validation. The submit handler
 * delegates to the Zustand auth store, which calls the Python API. Until that
 * API is live the call will fail and the form will show an inline error —
 * exactly the same code path that production will follow.
 *
 * After successful login the user is redirected to either:
 *   - the URL they originally tried to visit (preserved in `state.from`)
 *   - the homepage `/` if they came directly to /login
 */
import { useEffect, useReducer, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRight, CircleHelp, Eye, EyeOff, Lock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/stores/auth";
import { ApiError } from "@/api/client";

/**
 * Validation contract. Server-side validation lives in Django; this is just
 * the client-side guard so we never POST obviously empty payloads.
 */
const LoginSchema = z.object({
  username: z.string().min(1, "Required"),
  password: z.string().min(1, "Required"),
});

type LoginValues = z.infer<typeof LoginSchema>;

const WELCOME_MESSAGES = [
  "Welcome back.",
  "Good to see you.",
  "Ready when you are.",
  "How can Opus help you today?",
  "Sign in to continue.",
];

const TYPEWRITER_INTERVAL_MS = 35;
const INITIAL_WELCOME_MESSAGE = WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)]!;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuth((s) => s.login);
  // `authError` is set by the global 401 listener when a session expires
  // mid-use (separate from the per-submit error below). Shows as a banner
  // above the form so the user knows why they were bounced here.
  const authError = useAuth((s) => s.error);

  // Where to send the user after a successful login. Set by ProtectedRoute
  // when it bounces an unauthenticated visitor to /login.
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from
      ?.pathname ?? "/";

  const [submitError, setSubmitError] = useState<string | null>(null);
  type TypewriterState = { typedWelcome: string; showCaret: boolean };
  const [{ typedWelcome, showCaret }, dispatchTypewriter] = useReducer(
    (s: TypewriterState, action: Partial<TypewriterState> | "toggle-caret") =>
      action === "toggle-caret" ? { ...s, showCaret: !s.showCaret } : { ...s, ...action },
    { typedWelcome: "", showCaret: true },
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showAdHelp, setShowAdHelp] = useState(false);

  const welcomeMessage = INITIAL_WELCOME_MESSAGE;

  useEffect(() => {
    let index = 0;
    dispatchTypewriter({ typedWelcome: "" });
    const typeInterval = window.setInterval(() => {
      index += 1;
      dispatchTypewriter({ typedWelcome: welcomeMessage.slice(0, index) });
      if (index >= welcomeMessage.length) {
        window.clearInterval(typeInterval);
      }
    }, TYPEWRITER_INTERVAL_MS);

    const caretInterval = window.setInterval(() => {
      dispatchTypewriter("toggle-caret");
    }, 450);

    return () => {
      window.clearInterval(typeInterval);
      window.clearInterval(caretInterval);
    };
  }, [welcomeMessage]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { username: "", password: "" },
  });

  async function onSubmit(values: LoginValues) {
    setSubmitError(null);
    try {
      await login(values.username, values.password);
      navigate(from, { replace: true });
    } catch (err) {
      // Distinguish "your creds are wrong" (401/403) from "the server is broken".
      if (err instanceof ApiError) {
        setSubmitError(
          err.status === 401 || err.status === 403
            ? "Invalid username or password."
            : `Login failed (${err.status}). Try again or contact admin.`,
        );
      } else {
        setSubmitError("Network error. Check your connection and try again.");
      }
    }
  }

  return (
    <div className="flex flex-col gap-7">
      <div>
        <h1 className="m-0 min-h-[2.5rem] text-3xl font-extrabold tracking-tight text-aurora">
          {typedWelcome}
          <span className={showCaret ? "opacity-100" : "opacity-0"}>|</span>
        </h1>
        <div className="relative mt-1.5 flex items-center gap-2 text-sm text-fg-dim">
          <p className="m-0">Sign in with your AD credentials to continue.</p>
          <button
            type="button"
            aria-label="What are AD credentials?"
            aria-expanded={showAdHelp}
            onClick={() => setShowAdHelp(!showAdHelp)}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-fg-mute transition hover:bg-surface-2 hover:text-fg"
          >
            <CircleHelp size={14} />
          </button>
          {showAdHelp ? (
            <dialog
              open
              aria-label="AD credentials help"
              className="absolute left-0 top-7 z-10 m-0 w-full rounded-xl border border-line bg-surface p-3 text-xs leading-relaxed text-fg shadow-[0_16px_35px_-20px_rgba(0,0,0,0.55)]"
            >
              Your AD credentials are the credentials that you use to login to
              services like Microsoft Teams or Outlook. You can use different
              variations for your username such as john doe -&gt;
              jdoe@guest-tek.com or john.doe@guest-tek.com.
            </dialog>
          ) : null}
        </div>
      </div>

      {/* Banner from the global 401 handler — shown when the user was
          bounced here mid-session, distinct from the per-submit error
          rendered below the form. */}
      {authError ? (
        <div className="rounded-md border border-peach/30 bg-peach/10 px-3 py-2 text-[12px] text-peach">
          {authError}
        </div>
      ) : null}

      <form
        noValidate
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="username">Username</Label>
          <div className="relative">
            <User
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              id="username"
              type="text"
              autoComplete="username"
              autoFocus
              placeholder="neteng"
              className="pl-9"
              invalid={!!errors.username}
              {...register("username")}
            />
          </div>
          {errors.username ? (
            <span className="text-xs text-rose">{errors.username.message}</span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Lock
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              className="pl-9 pr-20"
              invalid={!!errors.password}
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-fg-mute transition hover:bg-surface-2 hover:text-fg"
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          {errors.password ? (
            <span className="text-xs text-rose">{errors.password.message}</span>
          ) : null}
        </div>

        {submitError ? (
          <div
            role="alert"
            className="rounded-lg border border-rose/30 bg-rose/10 px-3 py-2 text-sm text-rose"
          >
            {submitError}
          </div>
        ) : null}

        <Button type="submit" disabled={isSubmitting} className="mt-1">
          {isSubmitting ? "Signing in…" : "Sign in"}
          <ArrowRight size={14} />
        </Button>
      </form>

      <div className="text-center text-xs text-fg-mute">
        Trouble signing in? Ping the neteng team on Teams.
      </div>
    </div>
  );
}
